// The slide decks and reference pages published on hub.gazar.dev.
//
// **Why this list is hand-written rather than fetched.** The hub is a separate
// static site with its own deploy and no API, so there is nothing to query. The
// alternative was leaving all of it unlinked, which is what it was: checked on
// 7 Aug and again on 12 Aug 2026, `hub.gazar.dev` appeared **nowhere** in this
// source tree and the homepage carried **zero** links to it. Seventeen finished
// pages, every one of them carrying the newsletter CTA, reachable only by
// someone who attended the lesson or was handed the URL.
//
// The decks are already the largest single referrer to `/newsletter`, which
// converts at roughly 10% on roughly 30 visitors a month. Pointing existing site
// traffic at the thing that already converts best is the cheapest lever here:
// the asset is built, deployed and proven, and only the link was missing.
//
// **When you add a deck to the hub, add it here.** Every URL below returned 200
// on 12 Aug 2026; a dead link on this page is worse than no page, because it
// spends the visit it was built to earn.

export interface Deck {
  /** Path segment on hub.gazar.dev. */
  readonly slug: string
  readonly title: string
  /** One line, in plain terms: what the reader leaves with. */
  readonly blurb: string
}

export interface DeckGroup {
  readonly id: string
  readonly title: string
  /** Why this group exists, shown under its heading. */
  readonly intro: string
  readonly decks: readonly Deck[]
}

export const HUB_ORIGIN = 'https://hub.gazar.dev'

export const deckUrl = (slug: string) => `${HUB_ORIGIN}/${slug}/`

export const DECK_GROUPS: readonly DeckGroup[] = [
  {
    id: 'lightning-lessons',
    title: 'Lightning lessons',
    intro:
      'One idea, taught in half an hour, usually with a live demo. These are the slides from the free sessions.',
    decks: [
      {
        slug: 'ai-supply-chain',
        title: 'AI Supply Chain: Threat Model Your Agent Toolchain',
        blurb:
          'The trust boundary around an agent, and the two threat surfaces most teams only count as one.',
      },
      {
        slug: 'on-device-llms',
        title: 'On-Device LLMs: Ship Models to the Edge in 2026',
        blurb: 'When the model belongs on the device, and what it costs you when it does.',
      },
      {
        slug: 'spec-driven-development',
        title: 'Spec-Driven Development: Coding Agents That Ship',
        blurb: 'Writing the spec the agent works from, so the output is reviewable rather than surprising.',
      },
      {
        slug: 'prompting-like-an-engineer',
        title: 'Prompting Like an Engineer, Not a Wizard',
        blurb: 'Treating prompts as code: versioned, tested, and boring on purpose.',
      },
      {
        slug: 'your-first-llm-feature',
        title: 'Your First LLM Feature, Beyond the Quickstart',
        blurb: 'What the quickstart leaves out, which is most of what production needs.',
      },
      {
        slug: 'system-design-for-ai-agents',
        title: 'System Design for AI Agents, for Engineers',
        blurb: 'Designing the system around the model rather than bolting the model onto the system.',
      },
      {
        slug: 'system-design-senior-staff-architecture-patterns',
        title: 'Architecture Patterns for AI Systems, Senior to Staff',
        blurb: 'The patterns that come up in staff-level review, and how each one fails.',
      },
      {
        slug: 'convincing-system-design',
        title: 'Your System Design Answer Is Right But Unconvincing',
        blurb: 'Why a correct design still loses the room, and what to say instead.',
      },
      {
        slug: 'adr-promotion',
        title: 'The ADR That Gets You Promoted',
        blurb: 'Writing the decision record that makes your judgement visible to people who were not there.',
      },
    ],
  },
  {
    id: 'courses',
    title: 'Course decks',
    intro:
      'The full slide sets behind the cohorts. Free to read, whether or not you take the course.',
    decks: [
      {
        slug: 'llms-and-agents',
        title: 'Production-Ready Systems with LLMs and Agents',
        blurb: 'Every session: the model boundary, context, cost and latency, agent patterns, evals.',
      },
      {
        slug: 'llms-and-agents-4-weeks',
        title: 'Production-Ready Systems, four-week edition',
        blurb: 'The same material on the shorter four-week arc.',
      },
      {
        slug: 'senior-to-staff',
        title: 'Senior to Staff',
        blurb: 'The architecture judgement that separates a senior engineer from a staff one.',
      },
      {
        slug: 'senior-to-staff-4-weeks',
        title: 'Senior to Staff, four-week edition',
        blurb: 'The shorter run of the same course.',
      },
    ],
  },
  {
    id: 'reference',
    title: 'Reference',
    intro: 'Things worth keeping open in a tab rather than reading once.',
    decks: [
      {
        slug: 'system-designs',
        title: 'Worked System Designs',
        blurb: 'Real designs worked end to end, with the trade-offs left in.',
      },
      {
        slug: 'cheatsheets',
        title: 'Cheat Sheets',
        blurb: 'The numbers and rules of thumb that are quicker to look up than to remember.',
      },
      {
        slug: 'tools',
        title: 'Tool Guides',
        blurb: 'Field guides to the tools in the agent and LLM stack.',
      },
      {
        slug: 'swe-salaries',
        title: 'Senior Engineer Salaries: Pay vs Cost of Living, to 2035',
        blurb: 'An interactive comparison of pay against what it actually buys, city by city.',
      },
    ],
  },
]

/** Total across every group, so the page never hard-codes a count that drifts. */
export const DECK_COUNT = DECK_GROUPS.reduce((total, group) => total + group.decks.length, 0)
